"use client";

import * as React from "react";
import { Suspense, use } from "react";
import useSWR from "swr";

import styles from "./styles/SchoolFeedContactCard.module.scss";
import classNames from "classnames";
import { useStores } from "../../../stores/useStores";
import { blockObjectFactory } from "../../blocks/SchoolBlocks/blockObjectFactory";
import FontAwesome from "../../utilities/FontAwesome";
import ClickableLink from "../../utilities/ClickableLink";
import { getDirectionsUrl } from "../../../utils/SchoolBlocksUtilities";
import { observer } from "mobx-react";
import { useTranslateClassName } from "../../../hooks/useTranslateClassName";
import { ContactCardOrganization } from "../SchoolFeedDisplay";

const blockHeadClassName = classNames({
  "sb-blockHead": true,
  "sb-organization-color-block-bg": true,
  "sb-block-head-border": true,
  [styles.header]: true,
});

export function contactCardSwrKeyFromOrgId(orgId: string): string {
  return `contactCard/${orgId}`;
}

const ContactCardContent = observer(
  ({
    contactCardDataPromise,
  }: {
    contactCardDataPromise: Promise<ContactCardOrganization>;
  }) => {
    const initialOrganization = use(contactCardDataPromise);
    const { modalStore, userStore } = useStores();

    // Use SWR for real-time updates, with initial data from promise
    // The fetcher is null since we rely on optimistic updates via mutate() calls
    const { data: organization } = useSWR(
      contactCardSwrKeyFromOrgId(initialOrganization.id),
      null,
      {
        fallbackData: initialOrganization,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateOnMount: false,
      },
    );

    const handleClick = React.useCallback(() => {
      if (!organization) return;
      modalStore.addModal({
        type: "sbOrganization",
        orgId: organization.id,
        id: organization.id,
        blockObj: blockObjectFactory("organization", {
          id: organization.id,
          blockType: organization.type,
          blockModel: organization,
        } as IBlockObj),
      });
    }, [organization, modalStore]);

    const hasAddress = React.useCallback(() => {
      if (!organization) return false;

      if (organization?.locations && organization.locations.length > 0) {
        return organization.locations.some((location) => location.address);
      }

      if (organization?.detail?.address) {
        return true;
      }

      return false;
    }, [organization]);

    const hasDescription = (organization?.detail?.description?.length ?? 0) > 0;
    const isEditor = userStore.isEditor;

    if (!hasDescription && !isEditor) {
      return null;
    }

    return (
      <div
        className={classNames(
          styles.widget,
          styles.container,
          styles.schoolFeedContactCardContainer,
        )}
      >
        <div className={styles.cardContent}>
          <div className={blockHeadClassName}>{organization.title}</div>
          <div className={styles.descriptionSection}>
            <OrganizationDescription
              organization={organization}
              isLoading={false}
            />
          </div>
        </div>
        <div className={styles.buttonRow}>
          <button className={styles.actionButton} onClick={handleClick}>
            <FontAwesome
              prefix={"fas"}
              name={userStore.isEditor ? "fa-pencil" : "fa-info-circle"}
            />
          </button>
          {hasAddress() &&
            organization.locations.map((location, i) => {
              return (
                <ClickableLink
                  key={i}
                  className={styles.actionButton}
                  href={getDirectionsUrl(organization.title, location)}
                >
                  <FontAwesome prefix={"fas"} name={"fa-diamond-turn-right"} />
                </ClickableLink>
              );
            })}
          {organization?.third_party_links?.map((link, i) => (
            <ClickableLink
              key={i}
              className={styles.actionButton}
              href={link.value}
            >
              <FontAwesome prefix={"fas"} name={"fa-link"} /> {link.title}
            </ClickableLink>
          ))}
        </div>
      </div>
    );
  },
);

const OrganizationDescription = observer(({ organization, isLoading }) => {
  const hasDescription = organization?.detail?.description;
  const translateClassName = useTranslateClassName();
  const descriptionClassNames = classNames({
    [styles.orgDescription]: true,
    [styles.orgDescriptionPlaceholder]: !hasDescription,
    [translateClassName]: !!hasDescription,
  });

  if (isLoading) {
    return <div className={descriptionClassNames}>Loading...</div>;
  }

  return (
    <div className={descriptionClassNames}>
      {organization?.detail?.description ||
        "Click to add your organization's Address, Contact Info, and Website. This widget won't appear to users until a description is added."}
    </div>
  );
});

const LoadingContactCard = () => (
  <div
    className={classNames(
      styles.widget,
      styles.container,
      styles.schoolFeedContactCardContainer,
    )}
  >
    <div className={blockHeadClassName}>Loading...</div>
  </div>
);

export function SchoolFeedContactCard(props) {
  return (
    <Suspense fallback={<LoadingContactCard />}>
      <ContactCardContent
        contactCardDataPromise={props.contactCardDataPromise}
      />
    </Suspense>
  );
}

export default SchoolFeedContactCard;
